/**
 * EPIC-048 / 48.3 — Section « Favoris » de la sidebar gauche (§28.F.2).
 *
 * Hydrate dynamiquement `<ul aria-labelledby="nav-fav">` au boot de
 * chaque page. La section est masquée (classe `is-empty`) si :
 *   - le compte n'est pas `fully_active` §28.H, ou
 *   - la liste retournée par `GET /me/favorites` est vide,
 *   - ou l'appel échoue (mode dégradé silencieux).
 *
 * Écoute `favorites:changed` (CustomEvent émis par
 * `favorites-toggle`) pour ré-hydrater sans rechargement complet.
 */

import BaseController from "./base_controller.js";
import { api } from "../services/api.js";
import { queryParamsToSearch, normalizeQueryParams } from "../services/favorites/normalize.js";
import { writeCache, clearCache } from "../services/favorites/cache.js";

export default class FavoritesSidebarController extends BaseController {
  static targets = ["list"];

  async connect() {
    this.refresh = this.refresh.bind(this);
    window.addEventListener("favorites:changed", this.refresh);

    // Pas de this.hide() au boot : le script inline dans `sidebar-account.html`
    // a peut-être déjà pré-rendu la liste depuis localStorage pour éviter le
    // layout shift §28.F.2. On laisse l'état initial tel quel et on réconcilie
    // ci-dessous une fois l'API consultée.

    try {
      const me = await api.getMeCached();
      if (me?.organization?.onboardingStage !== "fully_active") {
        this.clearAndHide();
        return;
      }
    } catch (_e) {
      // Erreur transitoire : on garde l'éventuel pré-rendu et on retentera
      // au prochain chargement de page.
      return;
    }

    await this.refresh();
  }

  disconnect() {
    window.removeEventListener("favorites:changed", this.refresh);
  }

  async refresh() {
    let payload;
    try {
      payload = await api.get("/me/favorites");
    } catch (_e) {
      // Erreur transitoire : conserver le pré-rendu et le cache.
      return;
    }
    const favorites = Array.isArray(payload?.favorites) ? payload.favorites : [];
    if (favorites.length === 0) {
      this.clearAndHide();
      return;
    }
    favorites.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    this.render(favorites);
    this.show();
    writeCache(favorites, "fully_active");
  }

  clearAndHide() {
    if (this.hasListTarget) {
      while (this.listTarget.firstChild) {
        this.listTarget.removeChild(this.listTarget.firstChild);
      }
    }
    this.hide();
    clearCache();
  }

  render(favorites) {
    if (!this.hasListTarget) return;
    const currentPath = window.location.pathname;
    const currentSearch = queryParamsToSearch(normalizeQueryParams(window.location.search));

    while (this.listTarget.firstChild) {
      this.listTarget.removeChild(this.listTarget.firstChild);
    }

    for (const fav of favorites) {
      const li = document.createElement("li");
      const a = document.createElement("a");
      const search = queryParamsToSearch(fav.queryParams || {});
      a.href = `${fav.routePath}${search}`;
      a.textContent = fav.label;
      if (fav.routePath === currentPath && search === currentSearch) {
        a.setAttribute("aria-current", "page");
      }
      li.appendChild(a);
      this.listTarget.appendChild(li);
    }
  }

  show() {
    const section = this.element;
    if (section) section.classList.remove("is-empty");
  }

  hide() {
    const section = this.element;
    if (section) section.classList.add("is-empty");
  }
}
